<template>
    <v-form ref="form" v-model="valid" @submit.prevent>
        <v-tabs v-model="activeTab" class="mb-4">
            <v-tab value="settings">{{ $t('settings') }}</v-tab>
            <v-tab v-if="b2cId" value="contract">{{ $t('contract') }}</v-tab>
        </v-tabs>
        <v-tabs-window v-model="activeTab">
            <v-tabs-window-item value="settings">
                <SecondLevelTabs :tabs="secondLevelTabList">
                    <template v-slot:general_settings>
                        <B2CGeneralSettings v-model="formData" :b2c-id="b2cId" />
                    </template>
                    <template v-slot:payments>
                        <B2CPaymentsSettings v-model="formData" />
                    </template>
                    <template v-slot:notifications>
                        <B2CNotificationsSettings v-model="formData" />
                    </template>
                </SecondLevelTabs>
                <v-row class="pa-4 pt-10 gc-4">
                    <v-btn :disabled="isLoading || !valid" :loading="isSaving" data-cy="save" @click="save">
                        {{ $t('save') }}
                    </v-btn>
                    <v-btn :disabled="isLoading || isSaving" variant="text" data-cy="cancel" @click="emit('cancel')">
                        {{ $t('cancel') }}
                    </v-btn>
                </v-row>
            </v-tabs-window-item>
            <v-tabs-window-item value="contract">
                <CompanyContract :b2c-id="b2cId" contract-group-place="B2C">
                    <template v-slot:actions>
                        <v-btn
                            :disabled="isLoading || isSaving"
                            variant="text"
                            data-cy="cancel"
                            @click="emit('cancel')"
                        >
                            {{ $t('cancel') }}
                        </v-btn>
                    </template>
                </CompanyContract>
            </v-tabs-window-item>
        </v-tabs-window>
    </v-form>
</template>
<script setup lang="ts">
    import B2CGeneralSettings from '~~/layers/core/app/components/salesSettings/B2CGeneralSettings.vue'

    const props = defineProps<{
        b2cId?: number
    }>()

    const emit = defineEmits<{
        (e: 'save' | 'cancel'): void
    }>()

    const initDefaultData = () => ({
        name: null,
        clientNotificationsEnabled: true,
        clientNotificationsSender: null,
        managerNotification: true,
        url: null,
        paymentMethods: [],
    })

    const secondLevelTabList = ref<string[]>(['general_settings', 'payments', 'notifications'])
    const activeTab = ref<string>()
    const isSaving = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const formData = ref({})

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (props.b2cId) await loadData()
        else formData.value = initDefaultData()
    })

    async function loadData(): Promise<void> {
        isLoading.value = true
        try {
            formData.value = await $api.b2cSettings.get({b2cId: props.b2cId})
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        isSaving.value = true
        try {
            if (props.b2cId) {
                const data = clone(formData.value)
                delete data.defaultUrl

                await $api.b2cSettings.put(props.b2cId, data)
            } else {
                await $api.b2cSettings.post(formData.value)
            }
            $toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            $toast.error(e.msg || e.errors[0].message || t('error_message.save_error'))
        } finally {
            isSaving.value = false
        }
    }
</script>
